import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from '../context/TranslationContext';
import LanguageSelector from './LanguageSelector';
import { API_ENDPOINTS } from '../utils/config';
import '../styles/LoginPage.css';

interface LoginPageProps {
  onLogin: (userType: 'user' | 'admin') => void;
  onBack?: () => void;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLogin, onBack }) => {
  const { login } = useAuth();
  const { t } = useTranslation();
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [signUpTab, setSignUpTab] = useState<'company' | 'independent'>('company');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    name: '',
    registrationCode: '',
    signupType: 'company',
    resetEmail: ''
  });
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [verificationError, setVerificationError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isResetting, setIsResetting] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);
  const [unverifiedEmail, setUnverifiedEmail] = useState<string>('');
  const [isSsoRedirecting, setIsSsoRedirecting] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleMicrosoftLogin = async () => {
    setError(null);
    setSuccess(null);
    setIsSsoRedirecting(true);

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/microsoft/login`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(result.error || 'Unable to start Microsoft login');
      }

      const result = await response.json();
      const authorizationUrl = result?.data?.authorization_url;

      if (authorizationUrl) {
        window.location.href = authorizationUrl;
      } else {
        throw new Error('Missing authorization URL from server');
      }
    } catch (err) {
      console.error('Microsoft SSO initiation failed:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Unable to start Microsoft login. Please try again.'
      );
      setIsSsoRedirecting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      if (isLoginMode) {
        // Real login with backend
        const loginResult = await login(formData.email, formData.password);

        if (loginResult.success) {
          // User type will be determined in App.tsx useEffect when user state updates
        } else {
          // Check if it's a verification error
          if (loginResult.error && loginResult.error.includes('not verified')) {
            setUnverifiedEmail(formData.email);
            setError('Email is not verified. Automatically resending verification code...');

            // Automatically resend verification code
            await handleResendVerificationFromLogin();
          } else {
            let errorMessage = loginResult.error || t('common.invalid_email_password');

            // Improve error messages
            if (errorMessage.toLowerCase().includes('failed to fetch') || errorMessage.toLowerCase().includes('network error')) {
              errorMessage = 'Unable to connect to the server. Please check your internet connection.';
            } else if (errorMessage.toLowerCase().includes('invalid credentials') || errorMessage.includes('401')) {
              errorMessage = 'Incorrect email or password. Please try again.';
            }

            setError(errorMessage);
          }
        }
      } else {
        // Register logic
        if (formData.password !== formData.confirmPassword) {
          setError(t('common.passwords_not_match'));
          return;
        }

        if (signUpTab === 'company' && !formData.registrationCode) {
          setError(t('common.registration_code_required_error'));
          return;
        }

        try {
          const requestData = {
            name: formData.name,
            email: formData.email,
            password: formData.password,
            registration_code: signUpTab === 'company' ? formData.registrationCode : undefined,
            signup_type: formData.signupType
          };

          console.log('📤 Sending registration data:', requestData);

          const response = await fetch(`${API_ENDPOINTS.BASE_URL}/register`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(requestData)
          });

          const result = await response.json();

          if (response.ok) {
            // Registration successful, show verification modal
            setShowVerificationModal(true);
            setCountdown(600); // 10 minutes in seconds
            // Start countdown
            const timer = setInterval(() => {
              setCountdown(prev => {
                if (prev <= 1) {
                  clearInterval(timer);
                  return 0;
                }
                return prev - 1;
              });
            }, 1000);
          } else {
            console.error('Registration failed:', result);
            setError(result.message || t('common.registration_failed'));
          }
        } catch (error) {
          setError(t('common.registration_failed_try_again'));
        }
      }
    } catch (error) {
      setError(t('common.login_failed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerification = async (e: React.FormEvent) => {
    e.preventDefault();
    setVerificationError(null);
    setIsVerifying(true);

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: formData.email,
          code: verificationCode
        })
      });

      const result = await response.json();

      if (response.ok) {
        // Email verified successfully, now login
        const loginResult = await login(formData.email, formData.password);
        if (loginResult.success) {
          setShowVerificationModal(false);
          // User will be redirected automatically
        } else {
          setVerificationError(loginResult.error || t('common.login_failed_after_verification'));
        }
      } else {
        setVerificationError(result.message || t('common.verification_failed'));
      }
    } catch (error) {
      setVerificationError(t('common.verification_failed_try_again'));
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendVerification = async () => {
    setVerificationError(null);

    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: formData.email
        })
      });

      const result = await response.json();

      if (response.ok) {
        setCountdown(600); // Reset countdown to 10 minutes
        // Start new countdown
        const timer = setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
        setResendError(null);
      } else {
        setResendError(result.message || t('common.failed_to_resend_verification'));
      }
    } catch (error) {
      setVerificationError(t('common.resend_failed_try_again'));
    }
  };

  const handleResendVerificationFromLogin = async () => {
    console.log('🔄 Starting automatic resend verification...');
    setResendError(null);

    try {
      console.log('📧 Sending resend verification request for email:', unverifiedEmail);
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/resend-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: unverifiedEmail
        })
      });

      const result = await response.json();
      console.log('📧 Resend verification response:', result);

      if (response.ok) {
        console.log('✅ Resend verification successful, opening modal...');
        setResendError(null);
        setError(null);

        // Set the email for verification modal
        setFormData(prev => ({
          ...prev,
          email: unverifiedEmail
        }));

        // Show verification modal
        setShowVerificationModal(true);
        setCountdown(600); // 10 minutes in seconds

        // Start countdown
        const timer = setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setResendError(result.message || 'Failed to resend verification code');
      }
    } catch (error) {
      setResendError('Failed to resend verification code. Please try again.');
    }
  };

  const handleForgotPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setModalError(null);
    setIsLoading(true);

    try {
      const response = await fetch(API_ENDPOINTS.FORGOT_PASSWORD, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: formData.resetEmail
        })
      });

      const result = await response.json();

      if (response.ok) {
        // Store email for reset process
        setResetEmail(formData.resetEmail);
        // Close forgot password modal and open reset password modal
        setShowForgotPassword(false);
        setShowResetPassword(true);
        // Reset form
        setFormData(prev => ({
          ...prev,
          resetEmail: ''
        }));
      } else {
        setModalError(result.message || t('common.reset_failed'));
      }
    } catch (error) {
      setModalError(t('common.reset_failed_try_again'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setModalError(null);

    if (newPassword !== confirmNewPassword) {
      setModalError(t('common.passwords_not_match_reset'));
      return;
    }

    setIsResetting(true);

    try {
      const response = await fetch(API_ENDPOINTS.RESET_PASSWORD, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: resetEmail,
          reset_code: resetCode,
          new_password: newPassword
        })
      });

      const result = await response.json();

      if (response.ok) {
        // Success - close modal and show success message
        setShowResetPassword(false);
        setSuccess(t('common.password_reset_success'));
        setError(null);
        // Reset all form data
        setResetEmail('');
        setResetCode('');
        setNewPassword('');
        setConfirmNewPassword('');

        // Auto-hide success message after 5 seconds
        setTimeout(() => {
          setSuccess(null);
        }, 5000);
      } else {
        setModalError(result.message || t('common.reset_failed'));
      }
    } catch (error) {
      setModalError(t('common.reset_failed_try_again'));
    } finally {
      setIsResetting(false);
    }
  };

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      name: '',
      registrationCode: '',
      signupType: 'company',
      resetEmail: ''
    });
    setError(null);
  };

  const handleModeSwitch = (newMode: boolean) => {
    setIsLoginMode(newMode);
    resetForm();
    if (!newMode) {
      setSignUpTab('company'); // Default to company tab when switching to sign up
      setFormData(prev => ({
        ...prev,
        signupType: 'company',
        resetEmail: ''
      }));
    }
  };

  const handleTabSwitch = (newTab: 'company' | 'independent') => {
    setSignUpTab(newTab);
    setFormData(prev => ({
      ...prev,
      signupType: newTab
    }));
    setError(null);
  };

  return (
    <div className="login-page">
      {/* Animated Background */}
      <div className="animated-background">
        <div className="floating-shape shape-1"></div>
        <div className="floating-shape shape-2"></div>
        <div className="floating-shape shape-3"></div>
        <div className="floating-shape shape-4"></div>
        <div className="floating-shape shape-5"></div>
      </div>

      {/* Header */}
      <header className="landing-header">
        <div className="header-left">
          <div className="logo" onClick={onBack} style={{ cursor: 'pointer' }}>
            <img
              src="/favicon-32x32-white.png"
              alt="Logo"
              className="logo-icon"
            />
            <span className="logo-text">RFQ Agent</span>
          </div>
        </div>
        <div className="header-nav">
          <LanguageSelector />
        </div>
      </header>
      <div className="login-container">
        <div className="login-header">
          <h1>{t('common.procurement_dashboard')}</h1>
          <p>{t('common.welcome_back')}</p>
        </div>

        {!isLoginMode && (
          <div className="signup-tabs">
            <button
              className={`tab-btn ${signUpTab === 'company' ? 'active' : ''}`}
              onClick={() => handleTabSwitch('company')}
            >
              {t('common.company_signup')}
            </button>
            <button
              className={`tab-btn ${signUpTab === 'independent' ? 'active' : ''}`}
              onClick={() => handleTabSwitch('independent')}
            >
              {t('common.independent_signup')}
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          {!isLoginMode && (
            <>
              <div className="form-group">
                <label htmlFor="name">{t('common.full_name')}</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required={!isLoginMode}
                  placeholder={t('common.enter_full_name')}
                />
              </div>

              {signUpTab === 'company' && (
                <div className="form-group">
                  <label htmlFor="registrationCode">{t('common.registration_code_required')}</label>
                  <input
                    type="text"
                    id="registrationCode"
                    name="registrationCode"
                    value={formData.registrationCode}
                    onChange={handleInputChange}
                    required={signUpTab === 'company'}
                    placeholder={t('common.enter_registration_code')}
                  />
                  <small className="help-text">
                    {t('common.registration_code_help')}
                  </small>
                </div>
              )}

              {signUpTab === 'independent' && (
                <div className="form-group">
                  <div className="info-box">
                    <p>{t('common.independent_signup_info')}</p>
                  </div>
                </div>
              )}
            </>
          )}

          <div className="form-group">
            <label htmlFor="email">{t('common.email_address')}</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              placeholder={t('common.enter_email')}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">{t('common.password')}</label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              required
              placeholder={t('common.enter_password')}
            />
          </div>

          {!isLoginMode && (
            <div className="form-group">
              <label htmlFor="confirmPassword">{t('common.confirm_password')}</label>
              <input
                type="password"
                id="confirmPassword"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleInputChange}
                required={!isLoginMode}
                placeholder={t('common.confirm_your_password')}
              />
            </div>
          )}

          {error && (
            <div className="error-message" style={{ marginBottom: '16px', color: '#dc2626', fontSize: '14px', padding: '10px', backgroundColor: '#fee2e2', borderRadius: '6px', textAlign: 'center' }}>
              {error}
            </div>
          )}

          <button type="submit" className="login-submit-btn" disabled={isLoading}>
            {isLoading ? t('common.signing_in') : (isLoginMode ? t('common.sign_in') : t('common.create_account'))}
          </button>

          {isLoginMode && (
            <div className="text-center mt-4">
              <button
                type="button"
                onClick={() => setShowForgotPassword(true)}
                className="forgot-password-btn"
              >
                {t('common.forgot_password')}?
              </button>
              <div className="sso-divider">
                <span>or</span>
              </div>
              <button
                type="button"
                className="microsoft-login-btn"
                onClick={handleMicrosoftLogin}
                disabled={isLoading || isSsoRedirecting}
              >
                <span className="microsoft-icon" aria-hidden="true">
                  <svg viewBox="0 0 23 23" focusable="false">
                    <rect width="10" height="10" x="1" y="1" fill="#f25022" />
                    <rect width="10" height="10" x="12" y="1" fill="#7fba00" />
                    <rect width="10" height="10" x="1" y="12" fill="#00a4ef" />
                    <rect width="10" height="10" x="12" y="12" fill="#ffb900" />
                  </svg>
                </span>
                {isSsoRedirecting ? 'Redirecting...' : 'Sign in with Microsoft'}
              </button>
            </div>
          )}
        </form>



        {success && (
          <div className="success-message">
            <svg
              style={{ width: '16px', height: '16px' }}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            {success}
          </div>
        )}

        <div className="login-switch">
          <p>
            {isLoginMode ? t('common.dont_have_account') : t('common.already_have_account')}
            <button
              className="switch-btn"
              onClick={() => handleModeSwitch(!isLoginMode)}
            >
              {isLoginMode ? t('common.sign_up') : t('common.sign_in')}
            </button>
          </p>
        </div>
      </div>

      {/* Email Verification Modal */}
      {showVerificationModal && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '600px',
              width: '95%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              position: 'relative',
              zIndex: 10000,
              textAlign: 'center'
            }}
          >
            {/* Email Icon */}
            <div style={{ marginBottom: '24px' }}>
              <div
                style={{
                  width: '64px',
                  height: '64px',
                  backgroundColor: '#e3f2fd',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto'
                }}
              >
                <svg
                  style={{ width: '32px', height: '32px', color: '#1976d2' }}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
            </div>

            {/* Title */}
            <h2 style={{
              fontSize: '24px',
              fontWeight: 600,
              color: '#111827',
              marginBottom: '16px'
            }}>
              {t('common.email_verification')}
            </h2>

            {/* Message */}
            <p style={{
              color: '#6b7280',
              marginBottom: '24px',
              lineHeight: '1.5'
            }}>
              {t('common.verification_message').replace('{email}', formData.email)}
            </p>

            {/* Countdown */}
            <div style={{
              backgroundColor: '#fef3c7',
              padding: '12px',
              borderRadius: '8px',
              marginBottom: '24px'
            }}>
              <p style={{
                color: '#92400e',
                fontSize: '14px',
                margin: 0
              }}>
                {t('common.code_expires_in')}: <strong>{formatTime(countdown)}</strong>
              </p>
            </div>

            {/* Verification Form */}
            <form onSubmit={handleVerification}>
              <div style={{ marginBottom: '20px' }}>
                <label
                  htmlFor="verificationCode"
                  style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#374151',
                    marginBottom: '8px',
                    textAlign: 'left'
                  }}
                >
                  {t('common.verification_code')}
                </label>
                <input
                  type="text"
                  id="verificationCode"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value)}
                  placeholder="123456"
                  maxLength={6}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '16px',
                    textAlign: 'center',
                    letterSpacing: '2px',
                    fontFamily: 'monospace',
                    boxSizing: 'border-box'
                  }}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={isVerifying || countdown === 0}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  backgroundColor: countdown === 0 ? '#9ca3af' : '#000000',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 500,
                  cursor: countdown === 0 ? 'not-allowed' : 'pointer',
                  marginBottom: '12px'
                }}
              >
                {isVerifying ? t('common.verifying') : t('common.verify_email')}
              </button>
            </form>

            {/* Resend Button */}
            <button
              onClick={handleResendVerification}
              disabled={countdown > 0}
              style={{
                width: '100%',
                padding: '8px 16px',
                backgroundColor: 'transparent',
                color: countdown > 0 ? '#9ca3af' : '#000000',
                border: '1px solid',
                borderColor: countdown > 0 ? '#9ca3af' : '#000000',
                borderRadius: '8px',
                fontSize: '14px',
                cursor: countdown > 0 ? 'not-allowed' : 'pointer'
              }}
            >
              {countdown > 0
                ? t('common.resend_in').replace('{time}', formatTime(countdown))
                : t('common.resend_code')
              }
            </button>

            {/* Error Message */}
            {verificationError && (
              <div style={{
                color: '#dc2626',
                fontSize: '14px',
                marginTop: '16px',
                padding: '8px 12px',
                backgroundColor: '#fef2f2',
                borderRadius: '6px',
                border: '1px solid #fecaca'
              }}>
                {verificationError}
              </div>
            )}

            {/* Close Button */}
            <button
              onClick={() => setShowVerificationModal(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                color: '#9ca3af',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '4px'
              }}
            >
              <svg style={{ width: '24px', height: '24px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '600px',
              width: '95%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              position: 'relative',
              zIndex: 10000
            }}
          >
            <h2 style={{
              textAlign: 'center',
              marginBottom: '24px',
              color: '#1f2937',
              fontSize: '24px',
              fontWeight: 600
            }}>
              {t('common.forgot_password')}
            </h2>

            <p style={{
              textAlign: 'center',
              marginBottom: '24px',
              color: '#6b7280',
              fontSize: '14px',
              lineHeight: '1.5'
            }}>
              {t('common.enter_email_for_reset')}
            </p>

            <form onSubmit={handleForgotPasswordSubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: '#374151',
                  fontSize: '14px',
                  fontWeight: 500
                }}>
                  {t('common.email_address')}
                </label>
                <input
                  type="email"
                  name="resetEmail"
                  value={formData.resetEmail || ''}
                  onChange={handleInputChange}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '16px',
                    boxSizing: 'border-box'
                  }}
                  placeholder={t('common.enter_email')}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  backgroundColor: '#000000',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  marginBottom: '12px'
                }}
              >
                {isLoading ? t('common.sending') : t('common.send_reset_code')}
              </button>
            </form>

            {/* Error Message */}
            {modalError && (
              <div style={{
                color: '#dc2626',
                fontSize: '14px',
                marginTop: '16px',
                padding: '8px 12px',
                backgroundColor: '#fef2f2',
                borderRadius: '6px',
                border: '1px solid #fecaca'
              }}>
                {modalError}
              </div>
            )}

            {/* Close Button */}
            <button
              onClick={() => setShowForgotPassword(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                color: '#9ca3af',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '4px'
              }}
            >
              <svg style={{ width: '24px', height: '24px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPassword && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            zIndex: 9999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '32px',
              maxWidth: '600px',
              width: '95%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
              position: 'relative',
              zIndex: 10000
            }}
          >
            <h2 style={{
              textAlign: 'center',
              marginBottom: '24px',
              color: '#1f2937',
              fontSize: '24px',
              fontWeight: 600
            }}>
              {t('common.reset_password')}
            </h2>

            <p style={{
              textAlign: 'center',
              marginBottom: '24px',
              color: '#6b7280',
              fontSize: '14px',
              lineHeight: '1.5'
            }}>
              {t('common.enter_reset_code')}
            </p>

            <form onSubmit={handleResetPasswordSubmit}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: '#374151',
                  fontSize: '14px',
                  fontWeight: 500
                }}>
                  {t('common.verification_code')}
                </label>
                <input
                  type="text"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '16px',
                    textAlign: 'center',
                    letterSpacing: '2px',
                    fontFamily: 'monospace',
                    boxSizing: 'border-box'
                  }}
                  placeholder="123456"
                  maxLength={6}
                  required
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: '#374151',
                  fontSize: '14px',
                  fontWeight: 500
                }}>
                  {t('common.new_password')}
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '16px',
                    boxSizing: 'border-box'
                  }}
                  placeholder={t('common.enter_password')}
                  required
                />
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: '#374151',
                  fontSize: '14px',
                  fontWeight: 500
                }}>
                  {t('common.confirm_new_password')}
                </label>
                <input
                  type="password"
                  value={confirmNewPassword}
                  onChange={(e) => setConfirmNewPassword(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    border: '1px solid #d1d5db',
                    borderRadius: '8px',
                    fontSize: '16px',
                    boxSizing: 'border-box'
                  }}
                  placeholder={t('common.confirm_your_password')}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={isResetting}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  backgroundColor: '#000000',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  marginBottom: '12px'
                }}
              >
                {isResetting ? t('common.resetting') : t('common.reset_password')}
              </button>
            </form>

            {/* Error Message */}
            {modalError && (
              <div style={{
                color: '#dc2626',
                fontSize: '14px',
                marginTop: '16px',
                padding: '8px 12px',
                backgroundColor: '#fef2f2',
                borderRadius: '6px',
                border: '1px solid #fecaca'
              }}>
                {modalError}
              </div>
            )}

            {/* Close Button */}
            <button
              onClick={() => setShowResetPassword(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                color: '#9ca3af',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '4px'
              }}
            >
              <svg style={{ width: '24px', height: '24px' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage; 